import React from 'react';
import { Box, Container, makeStyles, useMediaQuery } from '@material-ui/core';
import { RouteComponentProps, useNavigate } from '@reach/router';
import { useTranslation } from 'react-i18next';
import SectionHeading from 'components/SectionHeading';
import ProductHeader from '../Header';
import ProductDetailedButton from '../DetailedButton/DetailedButton';
import AppNotFound from '../../../components/NotFound';
import { groupByType } from '../../utils';
import { sendEvent } from '../../../utils/google-analytics';
import { Product, ProductType } from '../../../types';
import i18n from 'i18n';
import Gallery from '../DetailsStep/Gallery';
import Steps from '../Steps/Steps';
import FAQSection from '../../../Homepage/components/FAQSection/FAQSection';

const useStyles = makeStyles((theme) => ({
  root: {},
  layout: {
    display: 'flex',
    gap: theme.spacing(4),
    padding: theme.spacing(0, 0, 4),
    flexWrap: 'nowrap',
  },
  list: {
    listStyle: 'none',
    padding: 0,
    '& li button svg': {
      fill: `${theme.palette.secondary.main} !important`,
    },
    '& li button svg *': {
      fill: `${theme.palette.secondary.main} !important`,
    },
  },
  listItem: {
    color: theme.palette.secondary.main,
  },
}));

// props
interface ProductTypeStepRouteProps {
  seo: string;
}
interface ProductTypeStepProps extends RouteComponentProps<ProductTypeStepRouteProps> {
  products: Product[];
}

/**
 * Product type step component.
 */
export default function ProductTypeStep({ products, seo }: ProductTypeStepProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const headingHtmlId = 'ProductTypeStep-heading';
  const types = groupByType(products, seo ?? '');
  const isMobile = useMediaQuery(({ breakpoints }) => breakpoints.down('sm'));
  const categoryImages = types[0]?.category?.images || [];

  if (!seo || types.length === 0) {
    return <AppNotFound />;
  }

  const onProductDetailClick = (x: ProductType) => {
    //@ts-ignore
    sendEvent('repair', `item_type_selected-type_${x.title}`, x.title);
    navigate(`./${seo}/${x.seo?.[i18n.language]}`);
  };

  function renderButtons() {
    const buttons = types.map((x, i) => (
      <Box component="li" key={i} mb={2} className={classes.listItem}>
        <ProductDetailedButton onClick={() => onProductDetailClick(x)} name={x.title} {...x} />
      </Box>
    ));
    return buttons;
  }

  return (
    <Box className={classes.root}>
      <Container maxWidth="xl">
      <Steps currentStep={1} />
      <Box mb={4} />

        <div
          className={classes.layout}
          style={{ flexWrap: isMobile ? 'wrap' : 'nowrap' }}
        >
          <Box width="100%">
            <ProductHeader categoryId={seo} />
            <Box mb={3} />
            <SectionHeading id={headingHtmlId} component="h1" size="large">
              {t('Select the item type')}
            </SectionHeading>
            <Box mb={3} />
            <Box
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                flexDirection: isMobile ? 'column-reverse' : 'row',
              }}
            >
              <Box width={isMobile ? '100%' : '50%'}>
                <ul aria-labelledby={headingHtmlId} className={classes.list}>
                  {renderButtons()}
                </ul>
              </Box>
              {!!categoryImages?.length && (
                <Box
                  width={isMobile ? '100%' : '46%'}
                  style={{ paddingBottom: isMobile ? '30px' : undefined }}
                >
                  <Gallery images={categoryImages} hideThumbs />
                </Box>
              )}
            </Box>
          </Box>
        </div>
      </Container>
      <FAQSection variant="product" />
    </Box>
  );
}
